import {
  type GenerationLog,
  GenerationLogSchema
} from '@stratumn/studio-shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { generationKeys } from '@/lib/query-keys';
import { HTTPWrapper } from '@/utils/http';

const POLL_INTERVAL = 3000;

const http = new HTTPWrapper<unknown>('process');

const generationLogsResponseSchema = z.array(GenerationLogSchema);

const fetchGenerationLogs = async (
  processId: string,
  generationId: string
): Promise<GenerationLog[]> => {
  const response = await http.get<unknown>(
    `/${processId}/generations/${generationId}/logs`
  );

  return generationLogsResponseSchema.parse(response ?? []);
};

type UseGenerationLogsOptions = {
  processId: string;
  generationId: string;
  /** Enables the query. Defaults to true. */
  enabled?: boolean;
  /**
   * When true, the query polls at POLL_INTERVAL. Defaults to false so
   * terminal generations fetch logs once without polling.
   */
  isRunning?: boolean;
};

type UseGenerationLogsResult = {
  logs: GenerationLog[];
  isLoading: boolean;
  isError: boolean;
  /**
   * Underlying query error, surfaced so callers can show a banner instead
   * of an empty `<LogViewer/>` indistinguishable from "no logs yet".
   */
  error: unknown;
  refetch: () => void;
};

export const useGenerationLogs = ({
  processId,
  generationId,
  enabled = true,
  isRunning = false
}: UseGenerationLogsOptions): UseGenerationLogsResult => {
  const query = useQuery({
    queryKey: generationKeys.logs(processId, generationId),
    queryFn: () => fetchGenerationLogs(processId, generationId),
    enabled,
    refetchInterval: enabled && isRunning ? POLL_INTERVAL : false
  });

  // Trigger one final fetch when the generation transitions out of the
  // running state, so the last logs emitted between the last poll and the
  // status flip are not lost. Without this, FAILED generations frequently
  // miss their final stderr lines and READY generations miss their summary.
  const wasRunning = useRef(isRunning);
  useEffect(() => {
    if (wasRunning.current && !isRunning && enabled) {
      query.refetch();
    }
    wasRunning.current = isRunning;
  }, [isRunning, enabled, query]);

  return {
    logs: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      query.refetch();
    }
  };
};
